import Cookies from 'js-cookie';

import { AUTH_COOKIES } from '../constants/cookies';
import { AUTH0_REAUTH_QUERY_PARAM, INACTIVITY_THRESHOLD_MS } from '../constants/session';
import { HttpMethod } from '../types';
import { apiFetch } from './apiFetch';
import { invokeAuth0LogoutHandler, invokeAuth0SessionRevalidator } from './auth0Session';
import {
    getAccessToken,
    isAccessTokenAuth0,
    removeAuthTokens,
    removeCartCookie,
    removeOrderGrooveToken,
    removeRegularAuthTokens,
    setLastActiveCookie,
} from './cookies';
import {
    clearAuth0ClaimsRefreshState,
    clearAutoshipDataCache,
    clearChatData,
    clearFavorites,
    clearLoyaltyData,
} from './localStorage';
import { logger } from './logger';
import { getPendingRedirectDestination, setPendingRedirectDestination } from './pendingRedirectDestination';
import { isRestrictedPathname } from './restrictedPathname';

const REFRESH_TIME = 5 * 60 * 1000;

export function isUserLoggedIn() {
    return !!getAccessToken();
}

export const isLastActiveExpired = () => {
    const lastActiveCookie = Cookies.get(AUTH_COOKIES.LAST_ACTIVE);

    // A stamped `lastActive` is the session clock. Checking `auth0Reauth` first
    // made every later login look expired, so `evaluateSession` logged the user
    // out before `setLastActiveCookie` could clear the flag.
    if (lastActiveCookie) {
        const lastActiveTimestamp = parseInt(lastActiveCookie, 10);
        return Date.now() - lastActiveTimestamp >= INACTIVITY_THRESHOLD_MS;
    }

    // Auth0 inactivity deletes `lastActive` and sets `auth0Reauth` so a missing
    // activity cookie still blocks silent SSO restore. Voluntary logout does
    // not keep that cookie, so a later sibling-app login can SSO back in.
    return Cookies.get(AUTH_COOKIES.AUTH0_REAUTH) === 'true';
};

export const isTokenExpiringSoon = () => {
    const tokenExpiry = Cookies.get(AUTH_COOKIES.TOKEN_EXPIRY);

    if (!tokenExpiry) {
        return false;
    }

    const tokenExpiryTime = parseInt(tokenExpiry, 10);
    return tokenExpiryTime - Date.now() <= REFRESH_TIME;
};

type AuthResponse = {
    success: boolean;
    data: {
        code: number;
    };
};

/**
 * Builds the path+query string used as the post-reauth destination.
 * When the caller omits both `destination` and `pending`, uses current pathname + search.
 * When a path-only destination matches the current pathname, appends current search (e.g. offers pagination).
 */
export function resolveSignInRedirectDestination(rawDest: string | undefined): string {
    if (globalThis.window === undefined) {
        return rawDest ?? '';
    }

    const pathname = globalThis.location.pathname;
    const search = globalThis.location.search || '';

    if (rawDest === undefined) {
        return pathname + search;
    }

    const queryIndex = rawDest.indexOf('?');
    if (queryIndex !== -1) {
        return rawDest;
    }

    const destPath = rawDest;
    if (destPath === pathname) {
        return pathname + search;
    }

    return rawDest;
}

function assignSignInWithRedirectUrl(destination: string, searchParams?: string, backUrl?: string) {
    const encoded = encodeURIComponent(destination);
    const params = [];
    if (searchParams) {
        params.push(searchParams);
    }
    if (backUrl) {
        params.push(`backUrl=${encodeURIComponent(backUrl)}`);
    }
    const querySuffix = params.length > 0 ? `&${params.join('&')}` : '';
    globalThis.location.assign(`/sign-in?redirectUrl=${encoded}${querySuffix}`);
}

export interface RedirectToSignIn {
    /** If false, assign /sign-in only. Default true. */
    addRedirectQueryParam?: boolean;
    /** Clear pending redirect destination before redirecting. Default false. */
    clearPending?: boolean;
    /** Destination for ?redirectUrl= (default: pending or current pathname). */
    destination?: string;
    forceRedirect?: boolean;
    /**
     * Only redirect from a restricted current path, or when the intended
     * destination (explicit or pending) is itself restricted. Default true.
     */
    onlyWhenRestricted?: boolean;
}

function isRestrictedDestination(path?: string | null): boolean {
    return Boolean(path && isRestrictedPathname(path));
}

/** True when inactivity/logout should send the user through `/sign-in`. */
function shouldSendToSignIn(onlyWhenRestricted: boolean, intendedDestination?: string | null): boolean {
    if (!onlyWhenRestricted) {
        return true;
    }

    return isRestrictedPathname(globalThis.location.pathname) || isRestrictedDestination(intendedDestination);
}

export const redirectToSignIn = ({
    addRedirectQueryParam = true,
    clearPending = false,
    destination,
    forceRedirect = false,
    onlyWhenRestricted = true,
}: RedirectToSignIn = {}) => {
    if (globalThis.window === undefined) {
        return;
    }

    if (forceRedirect && destination) {
        globalThis.location.assign(destination);
        return;
    }

    const pending = getPendingRedirectDestination();
    if (!shouldSendToSignIn(onlyWhenRestricted, destination ?? pending)) {
        return;
    }

    const pathname = globalThis.location.pathname;
    const logicalDest = destination ?? pending ?? pathname;
    if (clearPending && pending) {
        setPendingRedirectDestination(null);
    }
    if (logicalDest === '/sign-in' || !addRedirectQueryParam) {
        globalThis.location.assign('/sign-in');
        return;
    }

    const rawDest = destination ?? pending ?? undefined;
    const resolvedDest = resolveSignInRedirectDestination(rawDest);
    const search = globalThis.location.search || '';
    const currentFull = pathname + search;
    const omitBackUrl = resolvedDest === currentFull && search.length > 0;
    const backUrl = omitBackUrl ? undefined : currentFull;
    // Public-page inactivity ends the local session without navigating, so `/sign-in`
    // is often reached later with only `redirectUrl`. Stamp `reauth=1` whenever
    // the session is already expired so Auth0 cannot silently restore SSO.
    const reauthQuery = isLastActiveExpired() ? `${AUTH0_REAUTH_QUERY_PARAM}=1` : undefined;

    assignSignInWithRedirectUrl(resolvedDest, reauthQuery, backUrl);
};

/**
 * Resolves where the user should land after re-authenticating following session expiry
 * (e.g. inactivity). Returns `null` when the user should stay on the current page.
 *
 * Mirrors the branching in `redirectToSignIn` without performing navigation.
 */
export function resolveInactivityReauthReturnTo({
    skipRedirect = false,
    redirectUrl,
    onlyWhenRestricted = true,
}: {
    skipRedirect?: boolean;
    redirectUrl?: string;
    onlyWhenRestricted?: boolean;
} = {}): string | null {
    if (globalThis.window === undefined || skipRedirect) {
        return null;
    }

    const pending = getPendingRedirectDestination();
    if (!shouldSendToSignIn(onlyWhenRestricted, redirectUrl ?? pending)) {
        return null;
    }

    const pathname = globalThis.location.pathname;
    const addRedirectQueryParam = !!redirectUrl || !!pending;
    const logicalDest = redirectUrl ?? pending ?? pathname;

    if (logicalDest === '/sign-in' || !addRedirectQueryParam) {
        return pathname + (globalThis.location.search || '');
    }

    const rawDest = redirectUrl ?? pending ?? undefined;
    return resolveSignInRedirectDestination(rawDest);
}

/** Clears legacy cookies and client caches for an Auth0-owned session without calling Janrain APIs. */
export function clearLocalAuth0SessionState(callback?: () => void): void {
    removeRegularAuthTokens();
    removeOrderGrooveToken();
    callback?.();
    clearFavorites();
    clearLoyaltyData();
    clearAuth0ClaimsRefreshState();
    clearAutoshipDataCache();
    removeCartCookie();
}

export async function logoutUser({
    callback,
    forceRedirect,
    performApiLogout,
    redirectUrl,
    skipRedirect,
}: {
    callback?: () => void;
    /** @todo see if possible to combine forceRedirect and skipRedirect logic */
    forceRedirect?: boolean;
    performApiLogout?: (accessToken: string) => Promise<boolean>;
    redirectUrl?: string;
    skipRedirect?: boolean;
}) {
    try {
        const accessToken = getAccessToken();
        const isAuth0Token = isAccessTokenAuth0();
        const addRedirectQueryParam = !!redirectUrl || !!getPendingRedirectDestination();

        // Auth0-issued tokens delete `lastActive` and set `auth0Reauth` so `Auth0TokenSync`
        // cannot resurrect the access cookie when the registered handler is unavailable.
        if (isAuth0Token) {
            removeRegularAuthTokens();
        } else {
            removeAuthTokens();
        }
        removeOrderGrooveToken();
        callback?.();
        clearFavorites();
        clearLoyaltyData();
        clearAuth0ClaimsRefreshState();
        clearAutoshipDataCache();
        clearChatData();

        let logoutSuccess = true;
        if (accessToken && performApiLogout && !isAuth0Token) {
            logoutSuccess = await performApiLogout(accessToken);
        }

        if (logoutSuccess) {
            // Drop the cart before any redirect: the cookie is shared by every tab, and other tabs
            // reconcile against it to fall back to a guest cart once this one signs out.
            removeCartCookie();

            if (forceRedirect) {
                redirectToSignIn({ destination: '/sign-in', forceRedirect: true });
                return;
            }
        }

        if (!skipRedirect) {
            redirectToSignIn({
                addRedirectQueryParam,
                destination: redirectUrl,
            });
        }
    } catch (error) {
        logger.error('Error during logout', error);
        setPendingRedirectDestination(null);
    }
}

/** prevents multiple refresh requests */
let isRefreshingUserToken = false;

export async function refreshUserToken() {
    if (isRefreshingUserToken) {
        return;
    }

    try {
        isRefreshingUserToken = true;

        const response = await apiFetch<AuthResponse>(`/api/acp/auth/refresh-token`, {
            method: 'POST',
        });

        if ('success' in response && response.success) {
            logger.info('User tokens successfully refreshed', {});
        }
    } catch (error) {
        logger.error('Error during refresh token', error);
    } finally {
        isRefreshingUserToken = false;
    }
}

interface EvaluateSessionProps {
    skipRedirect?: boolean;
    redirectUrl?: string;
    performApiLogout?: (accessToken: string) => Promise<boolean>;
    httpMethod?: string;
}

function isPageDataLoadHttpMethod(httpMethod?: string): boolean {
    if (httpMethod === undefined) {
        return false;
    }

    const method = httpMethod.toUpperCase();
    return method === HttpMethod.GET || method === HttpMethod.HEAD || method === HttpMethod.OPTIONS;
}

async function handleExpiredLastActiveSession({
    skipRedirect,
    redirectUrl,
    performApiLogout,
    httpMethod,
}: EvaluateSessionProps): Promise<boolean> {
    if (isAccessTokenAuth0()) {
        const handled = await invokeAuth0LogoutHandler({ skipRedirect, redirectUrl });
        if (!handled) {
            // Handler not registered yet (or Auth0 React context unavailable): clear local state
            // and keep `auth0Reauth`. `Auth0TokenSync` completes `/v2/logout` when it mounts or
            // on its next sync pass.
            clearLocalAuth0SessionState();
        }
    } else {
        await logoutUser({ skipRedirect, redirectUrl, performApiLogout });
    }

    return isPageDataLoadHttpMethod(httpMethod);
}

async function isActiveAuth0SessionValid(sessionOptions: EvaluateSessionProps): Promise<boolean> {
    if (!isAccessTokenAuth0() || isPageDataLoadHttpMethod(sessionOptions.httpMethod)) {
        return true;
    }

    return invokeAuth0SessionRevalidator();
}

async function handleLoggedInSession(
    lastActiveExpired: boolean,
    sessionOptions: EvaluateSessionProps
): Promise<boolean> {
    if (lastActiveExpired) {
        return handleExpiredLastActiveSession(sessionOptions);
    }

    if (!(await isActiveAuth0SessionValid(sessionOptions))) {
        return false;
    }

    if (isTokenExpiringSoon() && !isAccessTokenAuth0()) {
        await refreshUserToken();
        if (!sessionOptions.skipRedirect) {
            setPendingRedirectDestination(null);
        }
        return true;
    }

    setLastActiveCookie();
    if (!sessionOptions.skipRedirect) {
        setPendingRedirectDestination(null);
    }

    return true;
}

/** Dispatched on `window` after `evaluateSession` finishes so client UI can re-read auth cookies. */
export const SESSION_EVALUATED_DOM_EVENT = 'ecom:session-evaluated';

/**
 * Checks inactivity and, for Auth0, remote refresh-token validity.
 *
 * Returns true when the caller may continue, including page-data requests after
 * inactivity logout. Returns false when a mutation or navigation must stop.
 */
export async function evaluateSession({
    skipRedirect = false,
    redirectUrl,
    performApiLogout,
    httpMethod,
}: EvaluateSessionProps): Promise<boolean> {
    let sessionStillValid = true;
    try {
        if (isUserLoggedIn()) {
            sessionStillValid = await handleLoggedInSession(isLastActiveExpired(), {
                skipRedirect,
                redirectUrl,
                performApiLogout,
                httpMethod,
            });
        }
    } finally {
        if (globalThis.window !== undefined) {
            globalThis.window.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));
        }
    }

    return sessionStillValid;
}
